import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  BlockStack,
  InlineStack,
  Text,
  Banner,
  Checkbox,
  Spinner,
  TextField,
  Divider,
  Select,
  Tabs,
  Button,
  ButtonGroup,
  Badge,
  Icon,
} from '@shopify/polaris';
import { 
  ViewIcon,
  HideIcon,
  MaximizeIcon,
  MinimizeIcon,
  ArrowLeftIcon,
  QuestionCircleIcon,
  LightbulbIcon,
  EmailIcon,
  StarFilledIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@shopify/polaris-icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useShop } from '../context/ShopContext';
import { useAuthenticatedFetch } from '../hooks/useApi';
import AiTemplateEditor from '../components/AiTemplateEditor';

// Best practices tips - now use translation keys
const getEmailBestPractices = (t: TFunction, type: 'abandoned_cart' | 'post_purchase') => {
  const practices = {
    abandoned_cart: [
      { 
        title: t('templateEditor.bestPractices.abandonedCart.timing.title', 'Send within 1-3 hours'),
        description: t('templateEditor.bestPractices.abandonedCart.timing.description', 'Abandoned cart emails sent within the first 3 hours have 40% higher conversion rates.'),
        icon: '⏰'
      },
      {
        title: t('templateEditor.bestPractices.abandonedCart.showItems.title', 'Show cart items prominently'),
        description: t('templateEditor.bestPractices.abandonedCart.showItems.description', 'Include images of the exact products left behind - visual reminders increase clicks by 65%.'),
        icon: '🖼️'
      },
      {
        title: t('templateEditor.bestPractices.abandonedCart.urgency.title', 'Create urgency without pressure'),
        description: t('templateEditor.bestPractices.abandonedCart.urgency.description', 'Use phrases like "Still thinking it over?" rather than aggressive countdown timers.'),
        icon: '💭'
      },
      {
        title: t('templateEditor.bestPractices.abandonedCart.recommendations.title', 'Offer personalized recommendations'),
        description: t('templateEditor.bestPractices.abandonedCart.recommendations.description', 'AI-powered suggestions based on browsing history can increase order value by 20%.'),
        icon: '✨'
      },
      {
        title: t('templateEditor.bestPractices.abandonedCart.subject.title', 'Keep subject lines short'),
        description: t('templateEditor.bestPractices.abandonedCart.subject.description', 'Subject lines with 6-10 words have the highest open rates. Use emojis sparingly.'),
        icon: '📝'
      },
    ],
    post_purchase: [
      {
        title: t('templateEditor.bestPractices.postPurchase.timing.title', 'Send within 24-48 hours'),
        description: t('templateEditor.bestPractices.postPurchase.timing.description', 'Post-purchase emails sent while excitement is high get 4x more engagement.'),
        icon: '⏰'
      },
      {
        title: t('templateEditor.bestPractices.postPurchase.thankYou.title', 'Thank them genuinely'),
        description: t('templateEditor.bestPractices.postPurchase.thankYou.description', 'A sincere thank you builds loyalty. Avoid immediately pushing more products.'),
        icon: '🙏'
      },
      {
        title: t('templateEditor.bestPractices.postPurchase.crossSell.title', 'Cross-sell complementary products'),
        description: t('templateEditor.bestPractices.postPurchase.crossSell.description', 'Recommend items that complement their purchase - "Goes great with..." converts well.'),
        icon: '🎯'
      },
      {
        title: t('templateEditor.bestPractices.postPurchase.orderDetails.title', 'Include order details'),
        description: t('templateEditor.bestPractices.postPurchase.orderDetails.description', 'Customers appreciate seeing what they ordered - it builds trust and reduces anxiety.'),
        icon: '📦'
      },
      {
        title: t('templateEditor.bestPractices.postPurchase.reviews.title', 'Ask for reviews later'),
        description: t('templateEditor.bestPractices.postPurchase.reviews.description', 'Wait 7-14 days before asking for reviews - let them experience the product first.'),
        icon: '⭐'
      },
    ],
  };
  return practices[type] || practices.abandoned_cart;
};

// Subject line examples - now use translation keys
const getSubjectLineExamples = (t: TFunction, type: 'abandoned_cart' | 'post_purchase') => {
  const examples = {
    abandoned_cart: [
      { subject: t('templateEditor.subjectExamples.abandonedCart.friendly', "Hey {{customer_name}}, you left something behind! 🛒"), style: t('templateEditor.subjectExamples.styles.friendly', 'Friendly') },
      { subject: t('templateEditor.subjectExamples.abandonedCart.simple', "Your cart is waiting for you"), style: t('templateEditor.subjectExamples.styles.simple', 'Simple') },
      { subject: t('templateEditor.subjectExamples.abandonedCart.discount', "Still thinking it over? Here's 10% off"), style: t('templateEditor.subjectExamples.styles.discount', 'With discount') },
      { subject: t('templateEditor.subjectExamples.abandonedCart.concerned', "Oops! Did something go wrong?"), style: t('templateEditor.subjectExamples.styles.concerned', 'Concerned') },
      { subject: t('templateEditor.subjectExamples.abandonedCart.urgency', "Complete your order before it's gone"), style: t('templateEditor.subjectExamples.styles.urgency', 'Urgency') },
    ],
    post_purchase: [
      { subject: t('templateEditor.subjectExamples.postPurchase.celebratory', "Thank you for your order, {{customer_name}}!"), style: t('templateEditor.subjectExamples.styles.celebratory', 'Celebratory') },
      { subject: t('templateEditor.subjectExamples.postPurchase.informative', "Your order is confirmed! Here's what's next"), style: t('templateEditor.subjectExamples.styles.informative', 'Informative') },
      { subject: t('templateEditor.subjectExamples.postPurchase.upsell', "You made a great choice! Plus, check this out..."), style: t('templateEditor.subjectExamples.styles.upsell', 'Upsell') },
      { subject: t('templateEditor.subjectExamples.postPurchase.community', "Welcome to the {{shop_name}} family!"), style: t('templateEditor.subjectExamples.styles.community', 'Community') },
      { subject: t('templateEditor.subjectExamples.postPurchase.surprise', "Your {{shop_name}} order + a little something extra"), style: t('templateEditor.subjectExamples.styles.surprise', 'Surprise') },
    ],
  };
  return examples[type] || examples.abandoned_cart;
};

// Pre-made template snippets - now with translated names/descriptions
const getTemplateSnippets = (t: TFunction) => ({
  urgencyBanner: {
    name: t('templateEditor.snippets.urgencyBanner.name', 'Urgency Banner'),
    description: t('templateEditor.snippets.urgencyBanner.description', 'Add a subtle urgency message'),
    html: `<div style="background: linear-gradient(90deg, #fef3c7, #fde68a); padding: 12px 20px; text-align: center; border-radius: 8px; margin: 20px 0;">
  <p style="margin: 0; color: #92400e; font-size: 14px;">⏰ <strong>Limited time:</strong> Complete your order in the next 24 hours to secure your items!</p>
</div>`,
  },
  trustBadges: {
    name: t('templateEditor.snippets.trustBadges.name', 'Trust Badges'),
    description: t('templateEditor.snippets.trustBadges.description', 'Add trust indicators'),
    html: `<div style="display: flex; justify-content: center; gap: 30px; margin: 30px 0; padding: 20px; background: #f9fafb; border-radius: 8px;">
  <div style="text-align: center;">
    <span style="font-size: 24px;">🔒</span>
    <p style="margin: 5px 0 0; font-size: 12px; color: #6b7280;">Secure Checkout</p>
  </div>
  <div style="text-align: center;">
    <span style="font-size: 24px;">🚚</span>
    <p style="margin: 5px 0 0; font-size: 12px; color: #6b7280;">Free Shipping</p>
  </div>
  <div style="text-align: center;">
    <span style="font-size: 24px;">↩️</span>
    <p style="margin: 5px 0 0; font-size: 12px; color: #6b7280;">Easy Returns</p>
  </div>
</div>`,
  },
  testimonial: {
    name: t('templateEditor.snippets.testimonial.name', 'Customer Testimonial'),
    description: t('templateEditor.snippets.testimonial.description', 'Add social proof'),
    html: `<div style="margin: 30px 0; padding: 25px; background: #f0fdf4; border-left: 4px solid #22c55e; border-radius: 0 8px 8px 0;">
  <p style="margin: 0 0 10px; font-style: italic; color: #166534; font-size: 16px;">"Absolutely love my purchase! Fast shipping and great quality. Will definitely buy again!"</p>
  <p style="margin: 0; color: #15803d; font-size: 14px;">— Sarah M. ⭐⭐⭐⭐⭐</p>
</div>`,
  },
  discountCode: {
    name: t('templateEditor.snippets.discountCode.name', 'Discount Code Box'),
    description: t('templateEditor.snippets.discountCode.description', 'Highlight a discount code'),
    html: `<div style="margin: 25px 0; padding: 25px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-radius: 12px; text-align: center;">
  <p style="margin: 0 0 10px; color: #fff; font-size: 14px; text-transform: uppercase; letter-spacing: 1px;">Exclusive Offer</p>
  <div style="background: #fff; display: inline-block; padding: 12px 30px; border-radius: 6px; margin: 10px 0;">
    <span style="font-family: monospace; font-size: 24px; font-weight: bold; color: #667eea; letter-spacing: 3px;">SAVE15</span>
  </div>
  <p style="margin: 10px 0 0; color: #e0e7ff; font-size: 14px;">Use this code for 15% off your next order!</p>
</div>`,
  },
  countdown: {
    name: t('templateEditor.snippets.countdown.name', 'Countdown Timer (Static)'),
    description: t('templateEditor.snippets.countdown.description', 'Create urgency with a visual timer'),
    html: `<div style="margin: 25px 0; text-align: center;">
  <p style="margin: 0 0 15px; color: {{text_color}}; font-size: 14px;">Your cart expires in:</p>
  <div style="display: inline-flex; gap: 10px;">
    <div style="background: {{primary_color}}; color: #fff; padding: 15px 20px; border-radius: 8px; text-align: center;">
      <div style="font-size: 28px; font-weight: bold;">23</div>
      <div style="font-size: 10px; text-transform: uppercase;">Hours</div>
    </div>
    <div style="background: {{primary_color}}; color: #fff; padding: 15px 20px; border-radius: 8px; text-align: center;">
      <div style="font-size: 28px; font-weight: bold;">59</div>
      <div style="font-size: 10px; text-transform: uppercase;">Mins</div>
    </div>
  </div>
</div>`,
  },
});

interface EmailTemplate {
  type: string;
  name: string;
  subject: string;
  previewText?: string;
  htmlBody: string;
  textBody?: string;
  headerImageUrl?: string;
  logoUrl?: string;
  primaryColor: string;
  backgroundColor: string;
  textColor: string;
  footerText?: string;
  recommendationCount: number;
  includeBundles: boolean;
  maxBundles: number;
  updatedAt?: string;
}

// Default HTML templates for each type — synced with server/Services/Email/EmailTemplateService.cs
const defaultTemplates: Record<string, string> = {
  abandoned_cart: `<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">
<html xmlns="http://www.w3.org/1999/xhtml">
<head>
    <meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>Complete Your Purchase</title>
    <!--[if !mso]><!-->
    <style type="text/css">
        @media only screen and (max-width: 620px) {
            .email-container { width: 100% !important; min-width: 100% !important; }
            .content-cell { padding: 24px 20px !important; }
            .header-cell { padding: 32px 20px 24px 20px !important; }
            .header-title { font-size: 22px !important; }
            .greeting-title { font-size: 20px !important; }
            .cart-img { width: 64px !important; height: 64px !important; }
            .cta-button { padding: 15px 32px !important; font-size: 15px !important; width: 80% !important; }
            .rec-cell { padding: 8px 4px !important; }
            .rec-img { width: 90px !important; height: 90px !important; }
            .rec-title { font-size: 12px !important; }
            .rec-price { font-size: 12px !important; }
            .bundle-img { width: 64px !important; height: 64px !important; }
            .footer-cell { padding: 24px 20px !important; }
        }
        @media only screen and (max-width: 400px) {
            .content-cell { padding: 20px 16px !important; }
            .header-cell { padding: 28px 16px 20px 16px !important; }
            .cart-img { width: 56px !important; height: 56px !important; }
            .cta-button { width: 90% !important; }
            .rec-img { width: 72px !important; height: 72px !important; }
        }
    </style>
    <!--<![endif]-->
    <!--[if mso]>
    <style type="text/css">
        table { border-collapse: collapse; }
        td { font-family: Arial, sans-serif; }
    </style>
    <![endif]-->
</head>
<body style="margin: 0; padding: 0; width: 100%; background-color: {{background_color}}; -webkit-text-size-adjust: 100%; -ms-text-size-adjust: 100%;">
    <!-- Background wrapper -->
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color: {{background_color}};">
        <tr>
            <td align="center" style="padding: 24px 12px;">
                <!--[if mso]>
                <table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" align="center"><tr><td>
                <![endif]-->
                <table role="presentation" class="email-container" width="100%" cellpadding="0" cellspacing="0" border="0" style="max-width: 600px; background-color: #ffffff;">
                    
                    <!-- ========== HEADER ========== -->
                    <tr>
                        <td class="header-cell" align="center" style="padding: 40px 40px 28px 40px; background-color: #6c4caf;">
                            <!--[if mso]>
                            <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td align="center" style="background-color: #6c4caf; padding: 40px 40px 28px 40px;">
                            <![endif]-->
                            <h1 class="header-title" style="margin: 0; color: #ffffff; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; font-size: 28px; font-weight: 700; line-height: 1.2; letter-spacing: -0.5px;">
                                {{shop_name}}
                            </h1>
                            <!--[if mso]>
                            </td></tr></table>
                            <![endif]-->
                        </td>
                    </tr>
                    
                    <!-- ========== GREETING ========== -->
                    <tr>
                        <td class="content-cell" style="padding: 32px 40px 16px 40px;">
                            <h2 class="greeting-title" style="margin: 0 0 14px 0; color: {{text_color}}; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; font-size: 24px; font-weight: 700; line-height: 1.3;">
                                Hey {{customer_first_name}}! &#128075;
                            </h2>
                            <p style="margin: 0; color: #555555; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; font-size: 15px; line-height: 1.6;">
                                We noticed you didn't complete your purchase. No worries &mdash; your items are still waiting for you!
                            </p>
                        </td>
                    </tr>
                    
                    <!-- ========== CART ITEMS ========== -->
                    <tr>
                        <td class="content-cell" style="padding: 16px 40px 24px 40px;">
                            <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border: 1px solid #eaeaea; border-radius: 8px;">
                                <!-- Cart Header -->
                                <tr>
                                    <td style="padding: 14px 20px; background-color: #fafafa; border-bottom: 1px solid #eaeaea;">
                                        <span style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; font-size: 13px; font-weight: 600; color: #888888; text-transform: uppercase; letter-spacing: 0.8px;">Your Cart</span>
                                    </td>
                                </tr>
                                <!-- Cart Items List -->
                                <tr>
                                    <td style="padding: 8px 20px;">
                                        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
                                            {{cart_items}}
                                        </table>
                                    </td>
                                </tr>
                                <!-- Cart Total -->
                                <tr>
                                    <td style="padding: 14px 20px; background-color: #fafafa; border-top: 1px solid #eaeaea;">
                                        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
                                            <tr>
                                                <td align="right">
                                                    <span style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; font-size: 18px; font-weight: 700; color: {{text_color}};">Total: {{cart_total}}</span>
                                                </td>
                                            </tr>
                                        </table>
                                    </td>
                                </tr>
                            </table>
                        </td>
                    </tr>
                    
                    <!-- ========== CTA BUTTON ========== -->
                    <tr>
                        <td align="center" style="padding: 8px 40px 36px 40px;">
                            <!--[if mso]>
                            <v:roundrect xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:word" href="{{checkout_url}}" style="height:52px;v-text-anchor:middle;width:300px;" arcsize="50%" strokecolor="#6c4caf" fillcolor="#6c4caf">
                            <w:anchorlock/>
                            <center style="color:#ffffff;font-family:Arial,sans-serif;font-size:16px;font-weight:bold;">Complete Your Purchase &#8594;</center>
                            </v:roundrect>
                            <![endif]-->
                            <!--[if !mso]><!-->
                            <a href="{{checkout_url}}" class="cta-button" style="display: inline-block; padding: 16px 48px; background-color: #6c4caf; color: #ffffff; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; font-size: 16px; font-weight: 700; text-decoration: none; text-align: center; border-radius: 28px; mso-hide: all;">
                                Complete Your Purchase &#8594;
                            </a>
                            <!--<![endif]-->
                        </td>
                    </tr>
                    
                    <!-- ========== DIVIDER ========== -->
                    <tr>
                        <td style="padding: 0 40px;">
                            <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
                                <tr><td style="border-top: 1px solid #eaeaea; font-size: 0; line-height: 0;">&nbsp;</td></tr>
                            </table>
                        </td>
                    </tr>
                    
                    <!-- ========== RECOMMENDATIONS ========== -->
                    <tr>
                        <td class="content-cell" style="padding: 32px 40px;">
                            <h3 style="margin: 0 0 4px 0; color: {{text_color}}; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; font-size: 18px; font-weight: 700; text-align: center;">
                                You might also like
                            </h3>
                            <p style="margin: 0 0 20px 0; color: #999999; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; font-size: 13px; text-align: center;">
                                Handpicked for you based on your cart
                            </p>
                            {{recommendations}}
                        </td>
                    </tr>
                    
                    <!-- ========== BUNDLES ========== -->
                    <tr>
                        <td class="content-cell" style="padding: 0 40px 24px 40px;">
                            {{bundles}}
                        </td>
                    </tr>
                    
                    <!-- ========== FOOTER ========== -->
                    <tr>
                        <td class="footer-cell" style="padding: 28px 40px; background-color: #f5f5f5; text-align: center;">
                            <p style="margin: 0 0 6px 0; color: #777777; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; font-size: 13px;">
                                Sent by <strong>{{shop_name}}</strong>
                            </p>
                            <p style="margin: 0 0 10px 0;">
                                <a href="{{shop_url}}" style="color: #6c4caf; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; font-size: 13px; text-decoration: underline;">Visit our store</a>
                                &nbsp;&middot;&nbsp;
                                <a href="{{unsubscribe_url}}" style="color: #999999; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; font-size: 13px; text-decoration: underline;">Unsubscribe</a>
                            </p>
                            <p style="margin: 0; color: #bbbbbb; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; font-size: 11px;">
                                &#169; {{year}} {{shop_name}}. All rights reserved.
                            </p>
                        </td>
                    </tr>
                </table>
                <!--[if mso]>
                </td></tr></table>
                <![endif]-->
            </td>
        </tr>
    </table>
</body>
</html>`,
  post_purchase: `<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">
<html xmlns="http://www.w3.org/1999/xhtml">
<head>
    <meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>Thanks for your order!</title>
    <!--[if mso]>
    <style type="text/css">
        table { border-collapse: collapse; }
    </style>
    <![endif]-->
</head>
<body style="margin: 0; padding: 0; width: 100%; background-color: {{background_color}};">
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color: {{background_color}};">
        <tr>
            <td align="center" style="padding: 40px 20px;">
                <!--[if mso]>
                <table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" align="center">
                <tr>
                <td>
                <![endif]-->
                <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="max-width: 600px; background-color: #ffffff;">
                    <!-- Header -->
                    <tr>
                        <td align="center" style="padding: 40px 40px 20px 40px; background-color: {{primary_color}};">
                            <h1 style="margin: 0; color: #ffffff; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; font-size: 24px; font-weight: 700;">
                                {{shop_name}}
                            </h1>
                        </td>
                    </tr>
                    
                    <!-- Thank You Message -->
                    <tr>
                        <td style="padding: 30px 40px 20px 40px; text-align: center; background-color: #ffffff;">
                            <h2 style="margin: 0 0 10px 0; color: {{text_color}}; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; font-size: 22px; font-weight: 700;">
                                Thank you for your order! &#127881;
                            </h2>
                            <p style="margin: 0; color: #666666; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; font-size: 16px;">
                                Order {{order_number}} from {{shop_name}}
                            </p>
                        </td>
                    </tr>
                    
                    <!-- Order Summary -->
                    <tr>
                        <td style="padding: 20px 40px;">
                            <h3 style="margin: 0 0 15px 0; color: {{text_color}}; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; font-size: 16px; font-weight: 600;">
                                What you ordered
                            </h3>
                            <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
                                {{order_items}}
                            </table>
                        </td>
                    </tr>
                    
                    <!-- Recommendations -->
                    <tr>
                        <td style="padding: 30px 40px; background-color: #f8f9fa;">
                            <h3 style="margin: 0 0 10px 0; color: {{text_color}}; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; font-size: 16px; font-weight: 600; text-align: center;">
                                &#128722; Complete the look
                            </h3>
                            <p style="margin: 0 0 20px 0; color: #666666; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; font-size: 14px; text-align: center;">
                                Customers who bought your items also loved these
                            </p>
                            {{recommendations}}
                        </td>
                    </tr>
                    
                    <!-- Bundles -->
                    <tr>
                        <td style="padding: 20px 40px;">
                            {{bundles}}
                        </td>
                    </tr>
                    
                    <!-- CTA Button -->
                    <tr>
                        <td align="center" style="padding: 20px 40px 30px 40px;">
                            <!--[if mso]>
                            <v:roundrect xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:word" href="{{shop_url}}" style="height:50px;v-text-anchor:middle;width:220px;" arcsize="10%" strokecolor="{{primary_color}}" fillcolor="{{primary_color}}">
                            <w:anchorlock/>
                            <center style="color:#ffffff;font-family:sans-serif;font-size:16px;font-weight:bold;">Continue Shopping</center>
                            </v:roundrect>
                            <![endif]-->
                            <!--[if !mso]><!-->
                            <a href="{{shop_url}}" style="display: inline-block; padding: 14px 35px; background-color: {{primary_color}}; color: #ffffff; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; font-size: 16px; font-weight: 600; text-decoration: none; text-align: center; mso-hide: all;">
                                Continue Shopping
                            </a>
                            <!--<![endif]-->
                        </td>
                    </tr>
                    
                    <!-- Footer -->
                    <tr>
                        <td style="padding: 30px 40px; background-color: #f1f1f1; text-align: center;">
                            <p style="margin: 0 0 10px 0; color: #666666; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; font-size: 14px;">
                                Thanks for shopping with {{shop_name}}!
                            </p>
                            <p style="margin: 0; color: #999999; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; font-size: 12px;">
                                <a href="{{shop_url}}" style="color: {{primary_color}}; text-decoration: underline;">Visit our store</a>
                            </p>
                        </td>
                    </tr>
                </table>
                <!--[if mso]>
                </td>
                </tr>
                </table>
                <![endif]-->
            </td>
        </tr>
    </table>
</body>
</html>`,
};

// Sample data for preview — matches server-side rendering from EmailTemplateService.cs
const sampleData: Record<string, string> = {
  customer_name: 'John Doe',
  customer_first_name: 'John',
  shop_name: 'My Amazing Store',
  shop_url: '#',
  cart_url: '#',
  checkout_url: '#',
  cart_total: '$129.97',
  order_total: '$149.99',
  order_number: '#1234',
  unsubscribe_url: '#',
  year: new Date().getFullYear().toString(),
  // Cart items — table-based layout matching backend RenderCartItems()
  cart_items: `
                <tr>
                    <td style="padding: 12px 0; border-bottom: 1px solid #f0f0f0;">
                        <table width="100%" cellpadding="0" cellspacing="0" border="0">
                            <tr>
                                <td width="76" valign="top" style="vertical-align: top;">
                                    <img class="cart-img" src="https://placehold.co/70x70/e2e8f0/475569?text=Headphones" alt="Wireless Headphones" width="70" height="70" style="display: block; border: 0; outline: none; border-radius: 8px; object-fit: cover;" />
                                </td>
                                <td valign="middle" style="vertical-align: middle; padding-left: 14px;">
                                    <a href="#" style="color: #333333; text-decoration: none; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; font-weight: 600; font-size: 14px; line-height: 1.3;">Wireless Headphones</a>
                                    <p style="margin: 4px 0 0 0; color: #888888; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; font-size: 13px;">Qty: 1</p>
                                </td>
                                <td width="80" align="right" valign="middle" style="vertical-align: middle;">
                                    <span style="color: #333333; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; font-weight: 700; font-size: 15px;">$79.99</span>
                                </td>
                            </tr>
                        </table>
                    </td>
                </tr>
                <tr>
                    <td style="padding: 12px 0; border-bottom: 1px solid #f0f0f0;">
                        <table width="100%" cellpadding="0" cellspacing="0" border="0">
                            <tr>
                                <td width="76" valign="top" style="vertical-align: top;">
                                    <img class="cart-img" src="https://placehold.co/70x70/e2e8f0/475569?text=Case" alt="Phone Case" width="70" height="70" style="display: block; border: 0; outline: none; border-radius: 8px; object-fit: cover;" />
                                </td>
                                <td valign="middle" style="vertical-align: middle; padding-left: 14px;">
                                    <a href="#" style="color: #333333; text-decoration: none; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; font-weight: 600; font-size: 14px; line-height: 1.3;">Phone Case</a>
                                    <p style="margin: 4px 0 0 0; color: #888888; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; font-size: 13px;">Qty: 2</p>
                                </td>
                                <td width="80" align="right" valign="middle" style="vertical-align: middle;">
                                    <span style="color: #333333; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; font-weight: 700; font-size: 15px;">$24.99</span>
                                </td>
                            </tr>
                        </table>
                    </td>
                </tr>`,
  // Order items — table-based layout matching backend RenderOrderItems()
  order_items: `
                <tr>
                    <td style="padding: 10px 0; border-bottom: 1px solid #eeeeee;">
                        <table width="100%" cellpadding="0" cellspacing="0" border="0">
                            <tr>
                                <td width="60" valign="top" style="vertical-align: top;">
                                    <img src="https://placehold.co/50x50/e2e8f0/475569?text=Shoes" alt="Premium Sneakers" width="50" height="50" style="display: block; border: 0; outline: none;" />
                                </td>
                                <td valign="top" style="vertical-align: top; padding-left: 12px;">
                                    <span style="color: #333333; font-weight: 500; font-size: 14px;">Premium Sneakers</span>
                                    <span style="color: #666666; font-size: 13px;"> × 1</span>
                                </td>
                            </tr>
                        </table>
                    </td>
                </tr>`,
  // Recommendations — table-based layout matching backend RenderRecommendations()
  recommendations: `
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr>
                <td class="rec-cell" width="130" align="center" valign="top" style="padding: 10px 6px; text-align: center; vertical-align: top;">
                    <a href="#" style="text-decoration: none; color: inherit;">
                        <img class="rec-img" src="https://placehold.co/110x110/e2e8f0/475569?text=Product+1" alt="Classic T-Shirt" width="110" height="110" style="display: block; border: 0; outline: none; margin: 0 auto 10px auto; border-radius: 10px; object-fit: cover;" />
                        <p class="rec-title" style="margin: 0 0 4px 0; font-size: 13px; color: #333333; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; font-weight: 500; line-height: 1.3;">Classic T-Shirt</p>
                        <p class="rec-price" style="margin: 0;"><span style="font-weight: 700; font-size: 15px; color: #333333; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;">$29.99</span></p>
                    </a>
                </td>
                <td class="rec-cell" width="130" align="center" valign="top" style="padding: 10px 6px; text-align: center; vertical-align: top;">
                    <a href="#" style="text-decoration: none; color: inherit;">
                        <img class="rec-img" src="https://placehold.co/110x110/e2e8f0/475569?text=Product+2" alt="Slim Fit Jeans" width="110" height="110" style="display: block; border: 0; outline: none; margin: 0 auto 10px auto; border-radius: 10px; object-fit: cover;" />
                        <p class="rec-title" style="margin: 0 0 4px 0; font-size: 13px; color: #333333; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; font-weight: 500; line-height: 1.3;">Slim Fit Jeans</p>
                        <p class="rec-price" style="margin: 0;"><span style="text-decoration: line-through; color: #aaaaaa; font-size: 12px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;">$99.99</span><br/><span style="color: #d63031; font-weight: 700; font-size: 15px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;">$79.99</span></p>
                    </a>
                </td>
                <td class="rec-cell" width="130" align="center" valign="top" style="padding: 10px 6px; text-align: center; vertical-align: top;">
                    <a href="#" style="text-decoration: none; color: inherit;">
                        <img class="rec-img" src="https://placehold.co/110x110/e2e8f0/475569?text=Product+3" alt="Running Shoes" width="110" height="110" style="display: block; border: 0; outline: none; margin: 0 auto 10px auto; border-radius: 10px; object-fit: cover;" />
                        <p class="rec-title" style="margin: 0 0 4px 0; font-size: 13px; color: #333333; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; font-weight: 500; line-height: 1.3;">Running Shoes</p>
                        <p class="rec-price" style="margin: 0;"><span style="font-weight: 700; font-size: 15px; color: #333333; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;">$129.99</span></p>
                    </a>
                </td>
                <td class="rec-cell" width="130" align="center" valign="top" style="padding: 10px 6px; text-align: center; vertical-align: top;">
                    <a href="#" style="text-decoration: none; color: inherit;">
                        <img class="rec-img" src="https://placehold.co/110x110/e2e8f0/475569?text=Product+4" alt="Sports Watch" width="110" height="110" style="display: block; border: 0; outline: none; margin: 0 auto 10px auto; border-radius: 10px; object-fit: cover;" />
                        <p class="rec-title" style="margin: 0 0 4px 0; font-size: 13px; color: #333333; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; font-weight: 500; line-height: 1.3;">Sports Watch</p>
                        <p class="rec-price" style="margin: 0;"><span style="font-weight: 700; font-size: 15px; color: #333333; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;">$199.99</span></p>
                    </a>
                </td>
    </tr></table>`,
  // Bundles — table-based layout matching backend RenderBundles()
  bundles: `
                <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin: 0 0 16px 0; border: 1px solid #eaeaea; border-radius: 8px; overflow: hidden;">
                    <tr>
                        <td style="padding: 18px 20px 12px 20px;">
                            <h3 style="margin: 0 0 4px 0; color: #333333; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; font-size: 16px; font-weight: 700;">Complete Bundle</h3>
                            <p style="margin: 0 0 14px 0; color: #d63031; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; font-size: 13px; font-weight: 600;">Save 20%</p>
                            <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
                                <tr>
                                    <td align="center" valign="top" style="text-align: center; padding: 4px 6px; vertical-align: top;">
                                        <img class="bundle-img" src="https://placehold.co/80x80/e2e8f0/475569?text=Yoga" alt="Yoga Mat" width="80" height="80" style="display: block; border: 0; outline: none; border-radius: 8px; margin: 0 auto;" />
                                        <p style="margin: 6px 0 0 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; font-size: 12px; color: #666666; line-height: 1.3;">Yoga Mat</p>
                                    </td>
                                    <td align="center" valign="top" style="text-align: center; padding: 4px 6px; vertical-align: top;">
                                        <img class="bundle-img" src="https://placehold.co/80x80/e2e8f0/475569?text=Bands" alt="Resistance Bands" width="80" height="80" style="display: block; border: 0; outline: none; border-radius: 8px; margin: 0 auto;" />
                                        <p style="margin: 6px 0 0 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; font-size: 12px; color: #666666; line-height: 1.3;">Resistance Bands</p>
                                    </td>
                                    <td align="center" valign="top" style="text-align: center; padding: 4px 6px; vertical-align: top;">
                                        <img class="bundle-img" src="https://placehold.co/80x80/e2e8f0/475569?text=Bottle" alt="Water Bottle" width="80" height="80" style="display: block; border: 0; outline: none; border-radius: 8px; margin: 0 auto;" />
                                        <p style="margin: 6px 0 0 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; font-size: 12px; color: #666666; line-height: 1.3;">Water Bottle</p>
                                    </td>
                                </tr>
                            </table>
                        </td>
                    </tr>
                    <tr>
                        <td style="padding: 12px 20px 18px 20px; text-align: center;">
                            <span style="text-decoration: line-through; color: #aaaaaa; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; font-size: 14px;">$104.97</span>
                            <span style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; font-size: 22px; font-weight: 700; color: #333333; margin-left: 8px;">$83.98</span>
                            <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin-top: 14px;">
                                <tr>
                                    <td align="center">
                                        <a href="#" style="display: inline-block; padding: 10px 28px; background-color: #6c4caf; color: #ffffff; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; font-size: 13px; font-weight: 600; text-decoration: none; border-radius: 20px;">Get This Bundle</a>
                                    </td>
                                </tr>
                            </table>
                        </td>
                    </tr>
                </table>`,
  footer_text: '',
};

export default function EmailTemplateEditorPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { templateType } = useParams<{ templateType: string }>();
  const { shop } = useShop();
  const fetchWithAuth = useAuthenticatedFetch();
  
  const [template, setTemplate] = useState<EmailTemplate | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState(0);
  
  // Preview panel state
  const [showPreview, setShowPreview] = useState(true);
  const [previewWidth, setPreviewWidth] = useState(45); // percentage - slightly less to give more editor space
  
  // Header section collapsed state - start collapsed for more editor space
  const [headerCollapsed, setHeaderCollapsed] = useState(true);
  
  // Editor theme state - persist preference
  const [editorTheme, setEditorTheme] = useState<'vs-dark' | 'vs-light'>(() => {
    return (localStorage.getItem('templateEditorTheme') as 'vs-dark' | 'vs-light') || 'vs-dark';
  });
  
  // Persist theme preference
  const handleThemeChange = useCallback((theme: 'vs-dark' | 'vs-light') => {
    setEditorTheme(theme);
    localStorage.setItem('templateEditorTheme', theme);
  }, []);
  
  // Load template
  useEffect(() => {
    const loadTemplate = async () => {
      if (!shop || !templateType) return;
      
      try {
        const templates = await fetchWithAuth('/api/email/templates') as EmailTemplate[];
        const found = templates.find(t => t.type === templateType);
        
        if (found) {
          setTemplate(found);
        } else {
          // Create default template — synced with backend GetDefaultAbandonedCartTemplate/GetDefaultPostPurchaseTemplate
          const typeName = templateType === 'abandoned_cart' ? 'Abandoned Cart' : 'Post-Purchase';
          setTemplate({
            type: templateType,
            name: typeName,
            subject: templateType === 'abandoned_cart' 
              ? "{{customer_first_name}}, you left something behind!"
              : "Thanks for your order, {{customer_first_name}}! Here's what others are buying 🎁",
            previewText: templateType === 'abandoned_cart'
              ? "Complete your purchase and get these personalized recommendations"
              : "Discover products that go perfectly with your purchase",
            htmlBody: defaultTemplates[templateType] || defaultTemplates.abandoned_cart,
            primaryColor: templateType === 'abandoned_cart' ? '#764ba2' : '#0070f3',
            backgroundColor: templateType === 'abandoned_cart' ? '#f0f0f5' : '#f7f7f7',
            textColor: '#333333',
            footerText: '',
            recommendationCount: 4,
            includeBundles: true,
            maxBundles: 1,
          });
        }
      } catch (err) {
        console.error('Error loading template:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadTemplate();
  }, [shop, templateType, fetchWithAuth]);

  const saveTemplate = useCallback(async () => {
    if (!shop || !template) return;
    
    setIsSaving(true);
    setSaveSuccess(false);
    setSaveError(null);
    
    try {
      await fetchWithAuth('/api/email/templates', {
        method: 'POST',
        body: {
          shop,
          type: template.type,
          subject: template.subject,
          previewText: template.previewText,
          htmlBody: template.htmlBody,
          textBody: template.textBody,
          headerImageUrl: template.headerImageUrl,
          logoUrl: template.logoUrl,
          primaryColor: template.primaryColor,
          backgroundColor: template.backgroundColor,
          textColor: template.textColor,
          footerText: template.footerText,
          recommendationCount: template.recommendationCount,
          includeBundles: template.includeBundles,
          maxBundles: template.maxBundles,
        },
      });
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving template:', err);
      setSaveError('Failed to save template. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [shop, template, fetchWithAuth]);

  const resetTemplate = useCallback(async () => {
    if (!templateType) return;
    
    // Reset to defaults synced with backend GetDefaultAbandonedCartTemplate/GetDefaultPostPurchaseTemplate
    const typeName = templateType === 'abandoned_cart' ? 'Abandoned Cart' : 'Post-Purchase';
    setTemplate({
      type: templateType,
      name: typeName,
      subject: templateType === 'abandoned_cart' 
        ? "{{customer_first_name}}, you left something behind!"
        : "Thanks for your order, {{customer_first_name}}! Here's what others are buying 🎁",
      previewText: templateType === 'abandoned_cart'
        ? "Complete your purchase and get these personalized recommendations"
        : "Discover products that go perfectly with your purchase",
      htmlBody: defaultTemplates[templateType] || defaultTemplates.abandoned_cart,
      primaryColor: templateType === 'abandoned_cart' ? '#764ba2' : '#0070f3',
      backgroundColor: templateType === 'abandoned_cart' ? '#f0f0f5' : '#f7f7f7',
      textColor: '#333333',
      footerText: '',
      recommendationCount: 4,
      includeBundles: true,
      maxBundles: 1,
    });
  }, [templateType]);

  const updateField = <K extends keyof EmailTemplate>(field: K, value: EmailTemplate[K]) => {
    if (template) {
      setTemplate({ ...template, [field]: value });
    }
  };

  // Generate preview HTML with sample data
  const previewHtml = useMemo(() => {
    if (!template) return '';
    
    let html = template.htmlBody;
    
    // Replace template variables with sample data — synced with backend SubstituteVariables()
    html = html.replace(/\{\{primary_color\}\}/g, template.primaryColor);
    html = html.replace(/\{\{background_color\}\}/g, template.backgroundColor);
    html = html.replace(/\{\{text_color\}\}/g, template.textColor);
    html = html.replace(/\{\{customer_name\}\}/g, sampleData.customer_name);
    html = html.replace(/\{\{customer_first_name\}\}/g, sampleData.customer_first_name);
    html = html.replace(/\{\{shop_name\}\}/g, sampleData.shop_name);
    html = html.replace(/\{\{shop_url\}\}/g, sampleData.shop_url);
    html = html.replace(/\{\{shop_logo\}\}/g, '');
    html = html.replace(/\{\{cart_url\}\}/g, sampleData.cart_url);
    html = html.replace(/\{\{checkout_url\}\}/g, sampleData.checkout_url);
    html = html.replace(/\{\{cart_items\}\}/g, sampleData.cart_items);
    html = html.replace(/\{\{cart_total\}\}/g, sampleData.cart_total);
    html = html.replace(/\{\{order_items\}\}/g, sampleData.order_items);
    html = html.replace(/\{\{order_total\}\}/g, sampleData.order_total);
    html = html.replace(/\{\{order_number\}\}/g, sampleData.order_number);
    html = html.replace(/\{\{recommendations\}\}/g, sampleData.recommendations);
    html = html.replace(/\{\{bundles\}\}/g, template.includeBundles ? sampleData.bundles : '');
    html = html.replace(/\{\{footer_text\}\}/g, template.footerText || sampleData.footer_text);
    html = html.replace(/\{\{unsubscribe_url\}\}/g, sampleData.unsubscribe_url);
    html = html.replace(/\{\{year\}\}/g, sampleData.year);
    
    return html;
  }, [template]);

  // Insert a snippet into the HTML body
  const insertSnippet = useCallback((snippetHtml: string) => {
    if (!template) return;
    
    // Find a good insertion point (before closing body tag or at the end)
    let newHtml = template.htmlBody;
    const insertionPoint = newHtml.lastIndexOf('</body>');
    
    if (insertionPoint > -1) {
      newHtml = newHtml.slice(0, insertionPoint) + '\n' + snippetHtml + '\n' + newHtml.slice(insertionPoint);
    } else {
      newHtml += '\n' + snippetHtml;
    }
    
    updateField('htmlBody', newHtml);
  }, [template]);

  // Apply a subject line example
  const applySubjectLine = useCallback((subject: string) => {
    if (!template) return;
    updateField('subject', subject);
  }, [template]);

  const tabs = [
    { id: 'content', content: `📝 ${t('templateEditor.tabs.content', 'Content')}`, panelID: 'content-panel' },
    { id: 'styling', content: `🎨 ${t('templateEditor.tabs.styling', 'Styling')}`, panelID: 'styling-panel' },
    { id: 'settings', content: `⚙️ ${t('templateEditor.tabs.settings', 'Settings')}`, panelID: 'settings-panel' },
    { id: 'help', content: `💡 ${t('templateEditor.tabs.help', 'Tips & Help')}`, panelID: 'help-panel' },
  ];

  // Get translated data
  const bestPractices = getEmailBestPractices(t, templateType as 'abandoned_cart' | 'post_purchase');
  const subjectExamples = getSubjectLineExamples(t, templateType as 'abandoned_cart' | 'post_purchase');
  const snippets = getTemplateSnippets(t);

  // Preview width controls
  const decreasePreviewWidth = () => setPreviewWidth(Math.max(30, previewWidth - 10));
  const increasePreviewWidth = () => setPreviewWidth(Math.min(70, previewWidth + 10));
  const togglePreview = () => setShowPreview(!showPreview);

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh',
        backgroundColor: '#f6f6f7'
      }}>
        <InlineStack gap="200" blockAlign="center">
          <Spinner size="small" />
          <Text as="p" variant="bodyMd">{t('common.loading')}</Text>
        </InlineStack>
      </div>
    );
  }

  if (!template) {
    return (
      <div style={{ padding: '20px' }}>
        <Banner tone="critical">{t('templateEditor.notFound', 'Template not found')}</Banner>
      </div>
    );
  }

  const templateTitle = templateType === 'abandoned_cart' 
    ? `🛒 ${t('templateEditor.abandonedCart', 'Abandoned Cart Email')}` 
    : `📦 ${t('templateEditor.postPurchase', 'Post-Purchase Email')}`;
  const editorWidth = showPreview ? `${100 - previewWidth}%` : '100%';

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100vh', 
      backgroundColor: '#f6f6f7',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        padding: '12px 20px',
        backgroundColor: '#fff',
        borderBottom: '1px solid #e1e3e5',
        flexShrink: 0
      }}>
        <InlineStack gap="400" blockAlign="center">
          <Button icon={ArrowLeftIcon} onClick={() => navigate('/app/email')} variant="tertiary">
            {t('templateEditor.backToSetup', 'Back')}
          </Button>
          <div>
            <Text as="h1" variant="headingLg">{templateTitle}</Text>
            <Text as="p" variant="bodySm" tone="subdued">{t('templateEditor.editDescription', 'Edit your email template with live preview')}</Text>
          </div>
        </InlineStack>
        
        <InlineStack gap="300">
          {/* Preview Controls */}
          <ButtonGroup>
            <Button 
              icon={showPreview ? HideIcon : ViewIcon} 
              onClick={togglePreview}
              variant="tertiary"
            >
              {showPreview ? t('templateEditor.hidePreview', 'Hide Preview') : t('templateEditor.showPreview', 'Show Preview')}
            </Button>
            {showPreview && (
              <>
                <Button 
                  icon={MinimizeIcon} 
                  onClick={decreasePreviewWidth}
                  variant="tertiary"
                  disabled={previewWidth <= 30}
                />
                <Text as="span" variant="bodySm" tone="subdued">
                  {previewWidth}%
                </Text>
                <Button 
                  icon={MaximizeIcon} 
                  onClick={increasePreviewWidth}
                  variant="tertiary"
                  disabled={previewWidth >= 70}
                />
              </>
            )}
          </ButtonGroup>
          
          <Button onClick={resetTemplate} tone="critical" variant="secondary">
            {t('templateEditor.resetToDefault', 'Reset to Default')}
          </Button>
          <Button onClick={saveTemplate} loading={isSaving} variant="primary">
            {t('templateEditor.saveTemplate', 'Save Template')}
          </Button>
        </InlineStack>
      </div>

      {/* Banners */}
      {(saveSuccess || saveError) && (
        <div style={{ padding: '12px 20px 0', flexShrink: 0 }}>
          {saveSuccess && (
            <Banner tone="success" onDismiss={() => setSaveSuccess(false)}>
              {t('templateEditor.templateSaved', 'Template saved successfully!')}
            </Banner>
          )}
        </div>
      )}

      {/* Main Content */}
      <div style={{ 
        display: 'flex', 
        flex: 1, 
        overflow: 'hidden',
        padding: '16px 20px'
      }}>
        {/* Editor Panel */}
        <div style={{ 
          width: showPreview ? editorWidth : '100%',
          backgroundColor: '#fff',
          borderRadius: '8px',
          border: '1px solid #e1e3e5',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          marginRight: showPreview ? '16px' : 0,
          transition: 'width 0.2s ease'
        }}>
          <div style={{ padding: '16px', borderBottom: '1px solid #e1e3e5' }}>
            <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab} />
          </div>
          
          <div style={{ flex: 1, overflow: 'auto', padding: selectedTab === 0 ? '0' : '20px' }}>
            {/* Content Tab */}
            {selectedTab === 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* Collapsible Subject and Preview fields */}
                <div style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e1e3e5' }}>
                  <button
                    onClick={() => setHeaderCollapsed(!headerCollapsed)}
                    style={{
                      width: '100%',
                      padding: '8px 20px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <InlineStack gap="200" blockAlign="center">
                      <Icon source={EmailIcon} tone="base" />
                      <Text as="span" variant="bodySm" fontWeight="semibold">
                        {t('templateEditor.content.emailSettings', 'Subject & Preview')}
                      </Text>
                      {headerCollapsed && template.subject && (
                        <Text as="span" variant="bodySm" tone="subdued">
                          {template.subject.length > 40 ? template.subject.substring(0, 40) + '...' : template.subject}
                        </Text>
                      )}
                    </InlineStack>
                    <Icon source={headerCollapsed ? ChevronDownIcon : ChevronUpIcon} />
                  </button>
                  
                  {!headerCollapsed && (
                    <div style={{ padding: '0 20px 16px' }}>
                      <BlockStack gap="300">
                        <TextField
                          label={t('templateEditor.content.subjectLine', 'Subject Line')}
                          value={template.subject}
                          onChange={(v) => updateField('subject', v)}
                          helpText={t('templateEditor.content.subjectLineHelp', 'Use {{customer_name}}, {{shop_name}} for personalization')}
                          autoComplete="off"
                        />
                        
                        <TextField
                          label={t('templateEditor.content.previewText', 'Preview Text')}
                          value={template.previewText || ''}
                          onChange={(v) => updateField('previewText', v)}
                          helpText={t('templateEditor.content.previewTextHelp', 'Short text shown in email client preview')}
                          autoComplete="off"
                        />
                      </BlockStack>
                    </div>
                  )}
                </div>
                
                {/* HTML Editor with Monaco + AI Assistant */}
                <div style={{ flex: 1, minHeight: '350px' }}>
                  <AiTemplateEditor
                    value={template.htmlBody}
                    onChange={(v) => updateField('htmlBody', v)}
                    templateType={templateType as 'abandoned_cart' | 'post_purchase'}
                    height="100%"
                    theme={editorTheme}
                    onThemeChange={handleThemeChange}
                    availableVariables={[
                      '{{customer_name}}',
                      '{{customer_first_name}}',
                      '{{shop_name}}',
                      '{{shop_url}}',
                      '{{primary_color}}',
                      '{{background_color}}',
                      '{{text_color}}',
                      '{{recommendations}}',
                      '{{bundles}}',
                      '{{cart_items}}',
                      '{{cart_total}}',
                      '{{cart_url}}',
                      '{{checkout_url}}',
                      '{{order_items}}',
                      '{{order_total}}',
                      '{{order_number}}',
                      '{{footer_text}}',
                      '{{unsubscribe_url}}',
                      '{{year}}',
                    ]}
                  />
                </div>
              </div>
            )}
            
            {/* Styling Tab */}
            {selectedTab === 1 && (
              <BlockStack gap="400">
                <Text as="h3" variant="headingSm">{t('templateEditor.styling.colors', 'Colors')}</Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  {t('templateEditor.styling.colorsDescription', 'These colors are used in the email template')}
                </Text>
                
                <InlineStack gap="400">
                  <div style={{ flex: 1 }}>
                    <TextField
                      label={t('templateEditor.styling.primaryColor', 'Primary Color')}
                      value={template.primaryColor}
                      onChange={(v) => updateField('primaryColor', v)}
                      autoComplete="off"
                      connectedLeft={
                        <input 
                          type="color" 
                          value={template.primaryColor}
                          onChange={(e) => updateField('primaryColor', e.target.value)}
                          style={{ width: 40, height: 36, border: 'none', cursor: 'pointer', borderRadius: '4px 0 0 4px' }}
                        />
                      }
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <TextField
                      label={t('templateEditor.styling.backgroundColor', 'Background Color')}
                      value={template.backgroundColor}
                      onChange={(v) => updateField('backgroundColor', v)}
                      autoComplete="off"
                      connectedLeft={
                        <input 
                          type="color" 
                          value={template.backgroundColor}
                          onChange={(e) => updateField('backgroundColor', e.target.value)}
                          style={{ width: 40, height: 36, border: 'none', cursor: 'pointer', borderRadius: '4px 0 0 4px' }}
                        />
                      }
                    />
                  </div>
                </InlineStack>
                
                <TextField
                  label={t('templateEditor.styling.textColor', 'Text Color')}
                  value={template.textColor}
                  onChange={(v) => updateField('textColor', v)}
                  autoComplete="off"
                  connectedLeft={
                    <input 
                      type="color" 
                      value={template.textColor}
                      onChange={(e) => updateField('textColor', e.target.value)}
                      style={{ width: 40, height: 36, border: 'none', cursor: 'pointer', borderRadius: '4px 0 0 4px' }}
                    />
                  }
                />
                
                <Divider />
                
                <Text as="h3" variant="headingSm">{t('templateEditor.styling.images', 'Images (Optional)')}</Text>
                
                <TextField
                  label={t('templateEditor.styling.logoUrl', 'Logo URL')}
                  value={template.logoUrl || ''}
                  onChange={(v) => updateField('logoUrl', v)}
                  placeholder="https://example.com/logo.png"
                  autoComplete="off"
                />
                
                <TextField
                  label={t('templateEditor.styling.headerImageUrl', 'Header Image URL')}
                  value={template.headerImageUrl || ''}
                  onChange={(v) => updateField('headerImageUrl', v)}
                  placeholder="https://example.com/header.jpg"
                  autoComplete="off"
                />
              </BlockStack>
            )}
            
            {/* Settings Tab */}
            {selectedTab === 2 && (
              <BlockStack gap="400">
                <Text as="h3" variant="headingSm">{t('templateEditor.settings.recommendations', 'Recommendations')}</Text>
                
                <Select
                  label={t('templateEditor.settings.numberOfRecommendations', 'Number of Recommendations')}
                  options={[
                    { label: t('templateEditor.settings.products2', '2 products'), value: '2' },
                    { label: t('templateEditor.settings.products3', '3 products'), value: '3' },
                    { label: t('templateEditor.settings.products4', '4 products'), value: '4' },
                    { label: t('templateEditor.settings.products6', '6 products'), value: '6' },
                  ]}
                  value={String(template.recommendationCount)}
                  onChange={(v) => updateField('recommendationCount', parseInt(v))}
                />
                
                <Divider />
                
                <Text as="h3" variant="headingSm">{t('templateEditor.settings.productBundles', 'Product Bundles')}</Text>
                
                <Checkbox
                  label={t('templateEditor.settings.includeBundles', 'Include Product Bundles')}
                  checked={template.includeBundles}
                  onChange={(v) => updateField('includeBundles', v)}
                  helpText={t('templateEditor.settings.includeBundlesHelp', 'Show \'Frequently Bought Together\' bundles in the email')}
                />
                
                {template.includeBundles && (
                  <Select
                    label={t('templateEditor.settings.maxBundles', 'Maximum Bundles to Show')}
                    options={[
                      { label: t('templateEditor.settings.bundles1', '1 bundle'), value: '1' },
                      { label: t('templateEditor.settings.bundles2', '2 bundles'), value: '2' },
                      { label: t('templateEditor.settings.bundles3', '3 bundles'), value: '3' },
                    ]}
                    value={String(template.maxBundles)}
                    onChange={(v) => updateField('maxBundles', parseInt(v))}
                  />
                )}
                
                <Divider />
                
                <Text as="h3" variant="headingSm">{t('templateEditor.content.footerText', 'Footer Text')}</Text>
                
                <TextField
                  label=""
                  labelHidden
                  value={template.footerText || ''}
                  onChange={(v) => updateField('footerText', v)}
                  multiline={2}
                  autoComplete="off"
                  helpText={t('templateEditor.settings.footerTextHelp', 'Text shown at the bottom of the email')}
                />
                
                <Divider />
                
                <Banner tone="info">
                  <BlockStack gap="200">
                    <Text as="p" variant="bodyMd" fontWeight="semibold">{t('templateEditor.settings.availableVariables', 'Available Variables')}</Text>
                    <Text as="p" variant="bodySm">
                      <code>{'{{customer_name}}'}</code> - {t('templateEditor.settings.variableCustomerName', "Customer's full name")}<br />
                      <code>{'{{customer_first_name}}'}</code> - {t('templateEditor.settings.variableCustomerFirstName', "Customer's first name")}<br />
                      <code>{'{{shop_name}}'}</code> - {t('templateEditor.settings.variableShopName', 'Your store name')}<br />
                      <code>{'{{shop_url}}'}</code> - {t('templateEditor.settings.variableShopUrl', 'Your store URL')}<br />
                      <code>{'{{cart_items}}'}</code> - {t('templateEditor.settings.variableCartItems', 'Cart products (abandoned cart)')}<br />
                      <code>{'{{cart_total}}'}</code> - {t('templateEditor.settings.variableCartTotal', 'Cart total amount')}<br />
                      <code>{'{{cart_url}}'}</code> - {t('templateEditor.settings.variableCartUrl', 'Cart recovery URL')}<br />
                      <code>{'{{checkout_url}}'}</code> - {t('templateEditor.settings.variableCheckoutUrl', 'Checkout URL')}<br />
                      <code>{'{{order_items}}'}</code> - {t('templateEditor.settings.variableOrderItems', 'Order products (post-purchase)')}<br />
                      <code>{'{{order_total}}'}</code> - {t('templateEditor.settings.variableOrderTotal', 'Order total amount')}<br />
                      <code>{'{{order_number}}'}</code> - {t('templateEditor.settings.variableOrderNumber', 'Order number')}<br />
                      <code>{'{{recommendations}}'}</code> - {t('templateEditor.settings.variableRecommendations', 'AI recommendations')}<br />
                      <code>{'{{bundles}}'}</code> - {t('templateEditor.settings.variableBundles', 'Product bundles')}<br />
                      <code>{'{{primary_color}}'}</code> - {t('templateEditor.settings.variablePrimaryColor', 'Primary color')}<br />
                      <code>{'{{background_color}}'}</code> - {t('templateEditor.settings.variableBackgroundColor', 'Background color')}<br />
                      <code>{'{{text_color}}'}</code> - {t('templateEditor.settings.variableTextColor', 'Text color')}<br />
                      <code>{'{{footer_text}}'}</code> - {t('templateEditor.settings.variableFooterText', 'Footer text')}<br />
                      <code>{'{{unsubscribe_url}}'}</code> - {t('templateEditor.settings.variableUnsubscribeUrl', 'Unsubscribe link')}<br />
                      <code>{'{{year}}'}</code> - {t('templateEditor.settings.variableYear', 'Current year')}
                    </Text>
                  </BlockStack>
                </Banner>
              </BlockStack>
            )}

            {/* Tips & Help Tab */}
            {selectedTab === 3 && (
              <BlockStack gap="600">
                {/* Best Practices Section */}
                <div>
                  <InlineStack gap="200" blockAlign="center">
                    <Icon source={LightbulbIcon} tone="warning" />
                    <Text as="h3" variant="headingMd">{t('templateEditor.help.bestPractices', 'Best Practices')}</Text>
                  </InlineStack>
                  <Text as="p" variant="bodySm" tone="subdued">
                    {t('templateEditor.help.bestPracticesDesc', 'Tips to maximize your email performance')}
                  </Text>
                  
                  <div style={{ marginTop: '16px' }}>
                    <BlockStack gap="300">
                      {bestPractices.map((tip, index) => (
                        <div 
                          key={index}
                          style={{ 
                            padding: '16px', 
                            backgroundColor: '#f9fafb', 
                            borderRadius: '8px',
                            border: '1px solid #e5e7eb'
                          }}
                        >
                          <InlineStack gap="300" blockAlign="start">
                            <span style={{ fontSize: '24px' }}>{tip.icon}</span>
                            <BlockStack gap="100">
                              <Text as="p" variant="bodyMd" fontWeight="semibold">{tip.title}</Text>
                              <Text as="p" variant="bodySm" tone="subdued">{tip.description}</Text>
                            </BlockStack>
                          </InlineStack>
                        </div>
                      ))}
                    </BlockStack>
                  </div>
                </div>

                <Divider />

                {/* Subject Line Examples */}
                <div>
                  <InlineStack gap="200" blockAlign="center">
                    <Icon source={EmailIcon} tone="info" />
                    <Text as="h3" variant="headingMd">{t('templateEditor.help.subjectLineIdeas', 'Subject Line Ideas')}</Text>
                  </InlineStack>
                  <Text as="p" variant="bodySm" tone="subdued">
                    {t('templateEditor.help.subjectLineIdeasDesc', 'Click any example to use it')}
                  </Text>
                  
                  <div style={{ marginTop: '16px' }}>
                    <BlockStack gap="200">
                      {subjectExamples.map((example, index) => (
                        <div 
                          key={index}
                          onClick={() => applySubjectLine(example.subject)}
                          style={{ 
                            padding: '12px 16px', 
                            backgroundColor: '#fff', 
                            borderRadius: '8px',
                            border: '1px solid #e5e7eb',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                          }}
                          onMouseOver={(e) => e.currentTarget.style.borderColor = '#667eea'}
                          onMouseOut={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
                        >
                          <InlineStack align="space-between" blockAlign="center">
                            <BlockStack gap="100">
                              <Text as="p" variant="bodyMd">{example.subject}</Text>
                              <Badge tone="info">{example.style}</Badge>
                            </BlockStack>
                            <Button size="slim" variant="plain">{t('templateEditor.help.useThis', 'Use this')}</Button>
                          </InlineStack>
                        </div>
                      ))}
                    </BlockStack>
                  </div>
                </div>

                <Divider />

                {/* Template Snippets */}
                <div>
                  <InlineStack gap="200" blockAlign="center">
                    <Icon source={StarFilledIcon} tone="success" />
                    <Text as="h3" variant="headingMd">{t('templateEditor.help.readyToUseSnippets', 'Ready-to-Use Snippets')}</Text>
                  </InlineStack>
                  <Text as="p" variant="bodySm" tone="subdued">
                    {t('templateEditor.help.snippetsDesc', 'Add these pre-made elements to your template')}
                  </Text>
                  
                  <div style={{ marginTop: '16px' }}>
                    <BlockStack gap="300">
                      {Object.entries(snippets).map(([key, snippet]) => (
                        <div 
                          key={key}
                          style={{ 
                            padding: '16px', 
                            backgroundColor: '#fff', 
                            borderRadius: '8px',
                            border: '1px solid #e5e7eb',
                          }}
                        >
                          <InlineStack align="space-between" blockAlign="center">
                            <BlockStack gap="100">
                              <Text as="p" variant="bodyMd" fontWeight="semibold">{snippet.name}</Text>
                              <Text as="p" variant="bodySm" tone="subdued">{snippet.description}</Text>
                            </BlockStack>
                            <Button 
                              onClick={() => insertSnippet(snippet.html)}
                              variant="secondary"
                            >
                              {t('templateEditor.help.insert', 'Insert')}
                            </Button>
                          </InlineStack>
                          
                          {/* Preview of the snippet */}
                          <div style={{ 
                            marginTop: '12px', 
                            padding: '12px', 
                            backgroundColor: '#f9fafb', 
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontFamily: 'monospace',
                            color: '#6b7280',
                            maxHeight: '80px',
                            overflow: 'hidden',
                            position: 'relative'
                          }}>
                            {snippet.html.slice(0, 150)}...
                            <div style={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              height: '30px',
                              background: 'linear-gradient(transparent, #f9fafb)'
                            }} />
                          </div>
                        </div>
                      ))}
                    </BlockStack>
                  </div>
                </div>

                <Divider />

                {/* Quick Tips */}
                <Banner tone="info">
                  <BlockStack gap="200">
                    <Text as="p" variant="bodyMd" fontWeight="semibold">
                      <Icon source={QuestionCircleIcon} /> {t('templateEditor.help.quickTips', 'Quick Tips')}
                    </Text>
                    <BlockStack gap="100">
                      <Text as="p" variant="bodySm">
                        • {t('templateEditor.help.tipTestFirst', 'Test before sending: Always send a test email to yourself first')}
                      </Text>
                      <Text as="p" variant="bodySm">
                        • {t('templateEditor.help.tipMobileFirst', 'Mobile-first: 60% of emails are opened on mobile - keep it simple')}
                      </Text>
                      <Text as="p" variant="bodySm">
                        • {t('templateEditor.help.tipOneCta', 'One clear CTA: Focus on one main action (e.g., "Complete Order")')}
                      </Text>
                      <Text as="p" variant="bodySm">
                        • {t('templateEditor.help.tipPersonalization', 'Personalization works: Emails with the customer\'s name get 26% more opens')}
                      </Text>
                      <Text as="p" variant="bodySm">
                        • {t('templateEditor.help.tipSmallImages', 'Keep images small: Large images may not load in all email clients')}
                      </Text>
                    </BlockStack>
                  </BlockStack>
                </Banner>
              </BlockStack>
            )}
          </div>
        </div>
        
        {/* Preview Panel */}
        {showPreview && (
          <div style={{ 
            width: `${previewWidth}%`,
            backgroundColor: '#fff',
            borderRadius: '8px',
            border: '1px solid #e1e3e5',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            transition: 'width 0.2s ease'
          }}>
            <div style={{ 
              padding: '16px 20px', 
              borderBottom: '1px solid #e1e3e5',
              backgroundColor: '#f9fafb'
            }}>
              <InlineStack gap="200" align="space-between" blockAlign="center">
                <Text as="h2" variant="headingMd">📧 {t('templateEditor.preview.title', 'Live Preview')}</Text>
                <Text as="p" variant="bodySm" tone="subdued">{t('templateEditor.preview.updatesAsYouType', 'Updates as you type')}</Text>
              </InlineStack>
            </div>
            
            {/* Subject Preview */}
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #e1e3e5' }}>
              <BlockStack gap="100">
                <Text as="p" variant="bodySm" tone="subdued">{t('templateEditor.preview.subject', 'Subject:')}</Text>
                <Text as="p" variant="bodyMd" fontWeight="semibold">
                  {template.subject
                    .replace(/\{\{customer_name\}\}/g, sampleData.customer_name)
                    .replace(/\{\{shop_name\}\}/g, sampleData.shop_name)}
                </Text>
              </BlockStack>
            </div>
            
            {/* Email Preview - Direct iframe without extra wrapper */}
            <div style={{ flex: 1, overflow: 'auto', backgroundColor: '#f3f4f6' }}>
              <iframe
                srcDoc={previewHtml}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  display: 'block'
                }}
                title={t('templateEditor.preview.title', 'Email Preview')}
                sandbox="allow-same-origin"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
